'use client'

import React, { useCallback, useState } from 'react'
import { ProductPageType } from '@productsite-beta/src/components/product-page'

import {
  useLayout,
  useSiblingEvent,
  useTranslations,
} from '@dustin-web/microsite-components/src/hooks'
import classNames from 'classnames'
import { AccessoryGroups } from './product-accessories'
import { GetProductAccessoriesQuery } from '@productsite-beta/src/graphql/generated/graphql-types'
import { AccessoryGroup } from './product-accessoriy-group'
import { AccessoryCategoryMenu } from './product-accessory-category-menu'

type Props = {
  product: ProductPageType
  acc: GetProductAccessoriesQuery['accessories']
  accessoryGroupsKeyed: AccessoryGroups | undefined
  moreGroups: any[]
}

export const ProductAccessoriesClient = ({
  product,
  moreGroups,
  acc,
  accessoryGroupsKeyed,
}: Props) => {
  const { translate } = useTranslations()
  const { responsive } = useLayout()

  // Client here

  const getAccessoriesForGroup = useCallback(
    (group: string) => accessoryGroupsKeyed && accessoryGroupsKeyed[group],
    []
  )

  const accessoryGroups = acc?.normalAccessories?.concat(acc?.insuranceAccessories || [])

  const accessoryGroupsCount = accessoryGroups ? accessoryGroups.length : 0
  const moreGroupsCount = moreGroups ? moreGroups.length : 0
  const onlyOneGroup = accessoryGroupsCount + moreGroupsCount === 1

  const dependentAccessories = product.availability?.isAvailableForSale
    ? acc?.dependentAccessories
    : []
  const topAccessories = acc?.topAccessories
  const productNearServicesAccessories = acc?.productNearServicesAccessories

  const [siblingEvent, mutateSibling] = useSiblingEvent<string>()
  const [activeMenuItem, setActiveMenuItem] = useState<string | null>()

  return (
    <div className="c-product-accessories u-bg-gray-02">
      <div className="container">
        <h2
          className={'h3 u-mb-32 d-none d-md-block u-overflow-ellipsis'}
          style={{ maxWidth: '700px' }}>
          {translate('Header_Accessories')} {product.displayName}
        </h2>
        <div className="row align-items-start">
          <div
            className={classNames('col-12', {
              'u-sticky': !responsive.isSmallScreen,
              'u-py-24': responsive.isSmallScreen,
              'col-lg-3': !onlyOneGroup,
            })}
            style={{ top: !responsive.isSmallScreen ? 120 : 0 }}>
            {accessoryGroupsKeyed && !onlyOneGroup && (
              <AccessoryCategoryMenu
                groups={accessoryGroupsKeyed}
                moreGroups={moreGroups}
                scrollToSiblingMutation={mutateSibling}
                activeMenuItem={activeMenuItem}
              />
            )}
          </div>

          <div className="col-12 col-lg-9">
            <div>
              {topAccessories && topAccessories.length > 0 && (
                <AccessoryGroup
                  group={'TopAccessories'}
                  title={translate('TopAccessories')}
                  accessories={topAccessories}
                  siblingScrollEvent={siblingEvent}
                  setActiveMenuItem={setActiveMenuItem}
                  activeMenuItem={activeMenuItem}
                  isFirstGroup={!dependentAccessories?.length}
                  onlyAvailableGroup={onlyOneGroup}
                />
              )}

              {dependentAccessories && dependentAccessories.length > 0 && (
                <AccessoryGroup
                  group={'Header_UniqueOfferings'}
                  title={`${translate('Header_UniqueOfferings')} ${product.displayName}`}
                  // titleShort={translate('UniqueOfferings')}
                  accessories={dependentAccessories}
                  mainProductErpIdentifier={product.productErpIdentifier}
                  siblingScrollEvent={siblingEvent}
                  setActiveMenuItem={setActiveMenuItem}
                  activeMenuItem={activeMenuItem}
                  isFirstGroup={!topAccessories?.length}
                  onlyAvailableGroup={onlyOneGroup}
                />
              )}

              {productNearServicesAccessories && productNearServicesAccessories.length > 0 && (
                <AccessoryGroup
                  group={'Header_ProductNearServices'}
                  title={`${translate('Header_ProductNearServices')}`}
                  accessories={productNearServicesAccessories}
                  mainProductErpIdentifier={product.productErpIdentifier}
                  siblingScrollEvent={siblingEvent}
                  setActiveMenuItem={setActiveMenuItem}
                  activeMenuItem={activeMenuItem}
                  isFirstGroup={!topAccessories?.length && !dependentAccessories?.length}
                  onlyAvailableGroup={onlyOneGroup}
                />
              )}
            </div>

            {accessoryGroupsKeyed &&
              Object.keys(accessoryGroupsKeyed).map((group, i) => (
                <AccessoryGroup
                  key={group}
                  group={group}
                  accessories={getAccessoriesForGroup(group) || []}
                  siblingScrollEvent={siblingEvent}
                  setActiveMenuItem={setActiveMenuItem}
                  activeMenuItem={activeMenuItem}
                  onlyAvailableGroup={onlyOneGroup}
                  isFirstGroup={
                    !topAccessories?.length &&
                    !dependentAccessories?.length &&
                    !productNearServicesAccessories?.length &&
                    i === 0
                  }
                />
              ))}
          </div>
        </div>
      </div>
    </div>
  )
}
